import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './hooks/useAuth'
import { useToast } from './hooks/useToast'
import { useCapabilities } from './hooks/useCapabilities'
import { useAppStore } from './store/appStore'
import { supabase, callSendReminder } from './lib/supabase'
import { PHASE_INFO } from './lib/types'
import { useSchoolYears } from './hooks/useSchoolYears'
import { formVersionForYear } from './lib/versions'
import { ImportPanel } from './components/teacher/ImportPanel'
import type { Iplp, ComponentRating, Evidence, Profile } from './lib/types'
import { LoginScreen } from './components/auth/LoginScreen'
import { Topbar } from './components/layout/Topbar'
import { Sidebar } from './components/layout/Sidebar'
import { ComponentEditor } from './components/teacher/ComponentEditor'
import { Modal, SignaturePad, Avatar, Icon } from './components/ui'

// Lazy-loaded heavy components
import { lazy, Suspense } from 'react'
const TeacherDashboard = lazy(() => import('./components/teacher/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })))
const GoalsPage = lazy(() => import('./components/teacher/GoalsPage').then(m => ({ default: m.GoalsPage })))
const SectionsPage = lazy(() => import('./components/teacher/SectionsPage').then(m => ({ default: m.SectionsPage })))
const SummaryPage = lazy(() => import('./components/teacher/SummaryPage').then(m => ({ default: m.SummaryPage })))
const HistoryPage = lazy(() => import('./components/teacher/HistoryPage').then(m => ({ default: m.HistoryPage })))
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const FormsPage = lazy(() => import('./components/admin/FormsPage').then(m => ({ default: m.FormsPage })))
const AssignmentPage = lazy(() => import('./components/admin/AssignmentPage').then(m => ({ default: m.AssignmentPage })))
const SettingsPage = lazy(() => import('./components/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))

const TODAY = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ color: 'var(--ink-4)', fontSize: 14 }}>Loading…</div>
    </div>
  )
}

export default function App() {
  const { user, profile, loading, signOut } = useAuth()
  const toast = useToast()
  const {
    role, phase, nav, reviewId, schoolYear, drawerOpen, settingsOpen, settingsTab,
    setRole, setReviewId, setNav, setDrawerOpen, closeSettings,
  } = useAppStore()
  const caps = useCapabilities()
  const { activeYear, yearInfo, dueFor } = useSchoolYears()
  const [signModal, setSignModal] = useState<'teacher' | 'admin' | null>(null)
  const [remindTarget, setRemindTarget] = useState<Profile | null>(null)
  const [remindMessage, setRemindMessage] = useState('')

  // Never let a non-admin-capable user sit in the Admin view (defense in
  // depth on top of RLS — the role switch is also hidden for them).
  useEffect(() => {
    if (role === 'admin' && !caps.canAdmin) {
      setRole('teacher')
      setReviewId(null)
      setNav({ page: 'overview' })
    }
  }, [role, caps.canAdmin, setRole, setReviewId, setNav])

  // Fetch teacher's IPLP for the selected school year
  const teacherId = role === 'teacher' ? user?.id : reviewId ?? null
  const { data: iplp } = useQuery<Iplp | null>({
    queryKey: ['iplp', teacherId, schoolYear],
    queryFn: async () => {
      if (!teacherId) return null
      const { data } = await supabase
        .from('iplps')
        .select('*')
        .eq('teacher_id', teacherId)
        .eq('school_year', schoolYear)
        .single()
      return (data as Iplp) ?? null
    },
    enabled: !!teacherId,
  })

  // Fetch ratings for the sidebar
  const { data: sidebarRatings = [] } = useQuery<ComponentRating[]>({
    queryKey: ['ratings', iplp?.id],
    queryFn: async () => {
      if (!iplp?.id) return []
      const { data } = await supabase
        .from('component_ratings')
        .select('*')
        .eq('iplp_id', iplp.id)
      return (data as ComponentRating[]) ?? []
    },
    enabled: !!iplp?.id,
  })

  // Fetch evidence for the sidebar
  const { data: sidebarEvidence = [] } = useQuery<Evidence[]>({
    queryKey: ['evidence', iplp?.id],
    queryFn: async () => {
      if (!iplp?.id) return []
      const { data } = await supabase
        .from('evidence')
        .select('id,component_code,type')
        .eq('iplp_id', iplp.id)
      return (data as Evidence[]) ?? []
    },
    enabled: !!iplp?.id,
  })

  // Fetch teacher profile for review banner
  const { data: reviewedProfile } = useQuery<Profile | null>({
    queryKey: ['profile', reviewId],
    queryFn: async () => {
      if (!reviewId) return null
      const { data } = await supabase.from('profiles').select('*').eq('id', reviewId).single()
      return (data as Profile) ?? null
    },
    enabled: !!reviewId,
  })

  async function handleSign(who: 'teacher' | 'admin') {
    if (!iplp?.id || !profile) return
    const now = new Date().toISOString()
    const update = who === 'teacher'
      ? { teacher_signed_at: now, teacher_signed_name: profile.name }
      : { admin_signed_at: now, admin_signed_name: profile.name }
    await supabase.from('iplps').update(update).eq('id', iplp.id)
    toast(who === 'teacher' ? 'Your IPLP is signed' : 'Evaluation signed')
    setSignModal(null)
  }

  async function handleSendReminder(channel: 'email' | 'in_app') {
    if (!remindTarget || !iplp) return
    const result = await callSendReminder({
      recipient_id: remindTarget.id,
      iplp_id: iplp.id,
      channel,
      message: remindMessage,
    })
    if (result.success) {
      toast(`Reminder sent to ${remindTarget.first_name} via ${channel === 'email' ? 'email' : 'in-app'}`)
    } else {
      toast('Failed to send reminder')
    }
    setRemindTarget(null)
    setRemindMessage('')
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--ink-4)', fontSize: 14 }}>
      Loading…
    </div>
  )
  if (!user) return <LoginScreen />

  const isAdmin = role === 'admin'
  const isReviewing = isAdmin && !!reviewId
  const ph = PHASE_INFO[phase]
  const yi = yearInfo(schoolYear)
  const yearReadOnly = yi.status === 'closed'

  function exitReview() {
    setReviewId(null)
    setNav({ page: 'dashboard' })
  }

  function renderMain() {
    // Admin root pages
    if (isAdmin && !isReviewing) {
      if (nav.page === 'forms') return <FormsPage />
      if (nav.page === 'assign') return <AssignmentPage />
      return <AdminDashboard />
    }

    // Plan history works across years even when the current year has no plan
    if (nav.page === 'history' && teacherId) {
      return (
        <HistoryPage
          teacherId={teacherId}
          profile={isReviewing ? (reviewedProfile ?? profile!) : profile!}
          isAdmin={isAdmin}
        />
      )
    }

    // Need IPLP for all teacher/review pages
    if (!iplp) {
      const showImport = yi.status === 'planning' && !isReviewing && teacherId && profile
      return (
        <div className="main">
          <div className="page-w">
            {showImport && (
              <ImportPanel
                teacherId={teacherId}
                districtId={profile.district_id}
                fromYear={activeYear}
                toYear={schoolYear}
                onImported={() => setNav({ page: 'overview' })}
              />
            )}
            <div style={{ paddingTop: showImport ? 20 : 60, textAlign: 'center', color: 'var(--ink-4)' }}>
              <Icon name="doc" size={36} style={{ marginBottom: 16, opacity: 0.4 }} />
              <div style={{ fontSize: 16, fontWeight: 600 }}>No plan for {yi.label}</div>
              <div style={{ fontSize: 13.5, marginTop: 8 }}>
                {isReviewing
                  ? `This staff member has no plan for the ${yi.label} cycle yet.`
                  : yi.status === 'planning'
                    ? `Your ${yi.label} plan hasn't been started yet. Import last year above, or begin fresh from any domain.`
                    : 'Your administrator hasn\'t set up your plan yet. Check back soon.'}
              </div>
            </div>
          </div>
        </div>
      )
    }

    const editLocked = (isReviewing && !isAdmin) || yearReadOnly

    if (nav.page === 'overview') return <TeacherDashboard iplpId={iplp.id} profile={isReviewing ? (reviewedProfile ?? profile!) : profile!} />
    if (nav.page === 'summary') return <SummaryPage iplpId={iplp.id} profile={isReviewing ? (reviewedProfile ?? profile!) : profile!} />
    if (nav.page === 'goals') return <GoalsPage iplpId={iplp.id} isAdmin={isAdmin} />
    if (nav.page === 'sections') return (
      <SectionsPage iplpId={iplp.id} isAdmin={isAdmin} readOnly={editLocked} onSign={setSignModal} />
    )
    if (nav.page === 'domain') {
      return (
        <ComponentEditor
          iplpId={iplp.id}
          componentCode={nav.code}
          phase={phase}
          isAdmin={isAdmin}
          readOnly={editLocked}
          formVersion={iplp.form_version ?? formVersionForYear(schoolYear)}
          formTemplateId={iplp.form_template_id}
        />
      )
    }
    return null
  }

  return (
    <div className="app">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      {/* Topbar */}
      <Topbar
        onSignOut={signOut}
        profile={profile}
      />

      {/* Read-only ribbon for closed years */}
      {yearReadOnly && (
        <div className="ro-ribbon">
          <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 36px' }} className="row center gap10">
            <Icon name="lock" size={14} />
            <span>
              Viewing the <b>{yi.label}</b> cycle — this is a closed record and is read-only.
            </span>
          </div>
        </div>
      )}

      {/* Admin review banner */}
      {isReviewing && reviewedProfile && (
        <div style={{ background: 'var(--navy-tint)', borderBottom: '1px solid var(--navy-tint-2)', padding: '10px 0' }}>
          <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 36px' }} className="row center gap14">
            <button className="btn btn-sm" onClick={exitReview}>
              <Icon name="arrowL" size={14} /> Back to roster
            </button>
            <div className="row center gap10">
              <Avatar name={reviewedProfile.name} size={28} />
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{reviewedProfile.name}</span>
              {reviewedProfile.subject && (
                <span className="muted" style={{ fontSize: 13 }}>· {reviewedProfile.subject}</span>
              )}
            </div>
            <div style={{ flex: 1 }} />
            <span className="muted" style={{ fontSize: 12.5 }}>
              Reviewing as administrator — score components & complete Sections 5–6.
            </span>
          </div>
        </div>
      )}

      <div className="shell">
        {/* Sidebar */}
        <Sidebar
          ratings={sidebarRatings}
          evidence={sidebarEvidence}
          phase={phase}
          profile={profile}
          onSignOut={signOut}
        />

        {/* Mobile drawer scrim */}
        {drawerOpen && <div className="side-scrim" onClick={() => setDrawerOpen(false)} />}

        {/* Main content */}
        <main className="main" id="main-content">
          <Suspense fallback={<PageLoader />}>
            {settingsOpen ? (
              <SettingsPage
                role={role}
                profile={profile}
                initialTab={settingsTab}
                onBack={closeSettings}
                onSignOut={signOut}
              />
            ) : (
              renderMain()
            )}
          </Suspense>
        </main>
      </div>

      {/* Sign modal */}
      {signModal && (
        <Modal
          title={signModal === 'teacher' ? 'Sign your IPLP' : 'Sign this evaluation'}
          icon="pen"
          onClose={() => setSignModal(null)}
          footer={
            <>
              <button className="btn" onClick={() => setSignModal(null)}>Cancel</button>
              <button className="btn btn-pri" onClick={() => handleSign(signModal)}>
                <Icon name="check" size={15} /> Confirm signature
              </button>
            </>
          }
        >
          <p className="muted" style={{ marginTop: 0, fontSize: 13.5, lineHeight: 1.55 }}>
            {signModal === 'teacher'
              ? 'Your signature acknowledges receipt of this evaluation. It does not necessarily indicate agreement — you may attach a written response within ten working days.'
              : 'Your signature finalizes this summative evaluation for HR. Ensure Sections 5 and 6 are complete.'}
          </p>
          <div style={{ marginTop: 16 }}>
            <SignaturePad />
          </div>
          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted mono" style={{ fontSize: 12 }}>
              {profile?.name} · {TODAY}
            </span>
          </div>
        </Modal>
      )}

      {/* Remind modal */}
      {remindTarget && (
        <Modal
          title="Send a reminder"
          icon="bell"
          onClose={() => { setRemindTarget(null); setRemindMessage('') }}
          footer={
            <>
              <button className="btn" onClick={() => { setRemindTarget(null); setRemindMessage('') }}>Cancel</button>
              <button className="btn" onClick={() => handleSendReminder('in_app')}>
                <Icon name="inbox" size={15} /> In-app
              </button>
              <button className="btn btn-pri" onClick={() => handleSendReminder('email')}>
                <Icon name="send" size={15} /> Send email
              </button>
            </>
          }
        >
          <div className="row center gap12" style={{ marginBottom: 16 }}>
            <Avatar name={remindTarget.name} size={40} />
            <div>
              <div style={{ fontWeight: 600 }}>{remindTarget.name}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {remindTarget.subject} · {remindTarget.school}
              </div>
            </div>
          </div>
          <label className="eyebrow">Message</label>
          <textarea
            className="ta"
            style={{ marginTop: 6 }}
            value={remindMessage}
            onChange={e => setRemindMessage(e.target.value)}
            placeholder={`Hi ${remindTarget.first_name}, a friendly reminder about your ${ph.label}…`}
            rows={4}
          />
          <div className="row center gap8" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--ink-3)' }}>
            <Icon name="clock" size={14} />
            Due {(() => {
              const iso = dueFor(schoolYear, phase)
              return iso
                ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : ph.due
            })()}
          </div>
        </Modal>
      )}
    </div>
  )
}
